import { Store } from 'lucide-react';
import { Suspense, lazy, useRef, useState } from 'react';
import { useI18n } from '../lib/i18n';
import { useSession } from '../lib/session';
import { cn } from '../lib/cn';
import { useDemo, maskStoreName } from '../lib/demo';
import { haptics } from '../lib/telegram';
import { Button } from './ui/Button';

// Панель открывается по нажатию, поэтому Radix и анимации не нужны на первом
// экране: откладываем их и экономим заметный кусок первой загрузки.
const Sheet = lazy(() => import('./ui/Sheet').then((module) => ({ default: module.Sheet })));

const STATUS_KEY = {
  online: 'store.online',
  degraded: 'store.degraded',
  offline: 'store.offline',
  unknown: 'store.unknown',
} as const;

const STATUS_COLOR = {
  online: 'bg-[var(--tone-green)]',
  degraded: 'bg-[var(--tone-amber)]',
  offline: 'bg-danger',
  unknown: 'bg-line-strong',
} as const;

/**
 * Переключатель магазинов — то, из-за чего это «мульти-СРМ»: один список
 * заказов из нескольких магазинов, и его можно сузить до одного.
 */
export function StoreFilter() {
  const [open, setOpen] = useState(false);
  // Один раз открыли — держим смонтированной, иначе теряется анимация закрытия.
  const [everOpened, setEverOpened] = useState(false);
  const { stores, selectedStores, toggleStore, selectOnlyStore, selectAllStores } = useSession();
  const { t } = useI18n();
  const { isDemo } = useDemo();
  const lastTapRef = useRef<{ storeId: string; time: number }>({ storeId: '', time: 0 });

  if (stores.length <= 1) return null;

  const handleStoreClick = (storeId: string) => {
    const now = Date.now();
    if (lastTapRef.current.storeId === storeId && now - lastTapRef.current.time < 380) {
      // Подвійний клік / тап — вибрати виключно цей магазин!
      haptics.success();
      selectOnlyStore(storeId);
      lastTapRef.current = { storeId: '', time: 0 };
    } else {
      lastTapRef.current = { storeId, time: now };
      haptics.select();
      toggleStore(storeId);
    }
  };

  const rawLabel =
    selectedStores.length === 0
      ? t('app.all')
      : selectedStores.length === 1
        ? (stores.find((store) => store.id === selectedStores[0])?.label ?? t('app.all'))
        : `${selectedStores.length}`;
  const label = selectedStores.length === 1 ? maskStoreName(rawLabel, isDemo) : rawLabel;

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          setEverOpened(true);
          setOpen(true);
        }}
        className="max-w-[9rem]"
      >
        <Store size={15} />
        <span className="truncate">{label}</span>
      </Button>

      {everOpened && (
        <Suspense fallback={null}>
          <Sheet open={open} onOpenChange={setOpen} title={t('settings.stores')}>
            <div className="space-y-1 pb-2">
              <button
                type="button"
                onClick={() => {
                  haptics.select();
                  selectAllStores();
                }}
                className={cn(
                  'flex w-full items-center justify-between rounded-xl px-3 py-3 text-left',
                  selectedStores.length === 0 ? 'bg-surface-2' : '',
                )}
              >
                <span className="font-medium">{t('app.all')}</span>
                {selectedStores.length === 0 && <span className="text-accent">✓</span>}
              </button>

              {stores.map((store) => {
                const checked = selectedStores.includes(store.id);
                return (
                  <button
                    key={store.id}
                    type="button"
                    onClick={() => handleStoreClick(store.id)}
                    onDoubleClick={() => {
                      haptics.success();
                      selectOnlyStore(store.id);
                    }}
                    className={cn(
                      'flex w-full items-center justify-between gap-3 rounded-xl px-3 py-3 text-left select-none',
                      checked ? 'bg-surface-2' : '',
                    )}
                  >
                    <span className="flex min-w-0 items-center gap-2.5">
                      {store.icon && !isDemo ? (
                        <img src={store.icon} alt="" className="h-5 w-5 shrink-0 rounded-full" />
                      ) : (
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ background: store.color }}
                        />
                      )}
                      <span className="min-w-0">
                        <span className="block truncate font-medium">
                          {maskStoreName(store.label, isDemo)}
                        </span>
                        <span className="text-muted flex items-center gap-1.5 text-[12px]">
                          <span
                            className={cn('h-1.5 w-1.5 rounded-full', STATUS_COLOR[store.status])}
                          />
                          {t(STATUS_KEY[store.status])}
                        </span>
                      </span>
                    </span>
                    {checked && <span className="text-accent shrink-0">✓</span>}
                  </button>
                );
              })}

              <div className="text-muted border-line mt-3 border-t pt-2.5 text-center text-[12px]">
                {t('settings.doubleTapHint')}
              </div>
            </div>
          </Sheet>
        </Suspense>
      )}
    </>
  );
}
